import React, { useEffect, useState, useCallback } from "react";
import { toast } from "react-toastify";
import {
  ImmunizationAPI,
  ImmunizationDose,
  isDoseBlocked,
} from "../services/immunization.service";

interface ImmunizationTabProps {
  patientId: string;
  editable: boolean;
}

const STATUS_STYLES: Record<string, string> = {
  administered: "bg-emerald-50 text-emerald-700 border-emerald-200",
  skipped: "bg-slate-100 text-slate-600 border-slate-200",
  overdue: "bg-amber-50 text-amber-700 border-amber-200",
  blocked: "bg-rose-50 text-rose-700 border-rose-200",
  due: "bg-sky-50 text-sky-700 border-sky-200",
};

const STATUS_LABELS: Record<string, string> = {
  administered: "Administered",
  skipped: "Skipped",
  overdue: "Overdue",
  blocked: "Blocked",
  due: "Due",
};

function StatusChip({ status }: { status: string }) {
  return (
    <span
      className={`inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-full border ${
        STATUS_STYLES[status] || STATUS_STYLES.due
      }`}
    >
      {STATUS_LABELS[status] || status}
    </span>
  );
}

function AdministerForm({
  dose,
  onSubmit,
  onCancel,
}: {
  dose: ImmunizationDose;
  onSubmit: (date: string, batchNo: string, overrideReason: string) => void;
  onCancel: () => void;
}) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [batchNo, setBatchNo] = useState("");
  const [overrideReason, setOverrideReason] = useState("");
  const [showOverride, setShowOverride] = useState(false);

  return (
    <div className="mt-2 p-3 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
      <div className="flex items-center gap-2">
        <label className="text-xs text-slate-600 w-24">Date given</label>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="text-sm border border-slate-200 rounded-lg px-2 py-1 flex-1"
        />
      </div>
      <div className="flex items-center gap-2">
        <label className="text-xs text-slate-600 w-24">Batch no.</label>
        <input
          type="text"
          value={batchNo}
          onChange={(e) => setBatchNo(e.target.value)}
          placeholder="optional"
          className="text-sm border border-slate-200 rounded-lg px-2 py-1 flex-1"
        />
      </div>
      {showOverride && (
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-600 w-24">Override reason</label>
          <input
            type="text"
            value={overrideReason}
            onChange={(e) => setOverrideReason(e.target.value)}
            placeholder="required to force through an advisory block"
            className="text-sm border border-slate-200 rounded-lg px-2 py-1 flex-1"
          />
        </div>
      )}
      <div className="flex items-center gap-2 pt-1">
        <button
          onClick={() => onSubmit(date, batchNo, overrideReason)}
          className="text-xs font-medium bg-[#0066ff] text-white px-3 py-1.5 rounded-lg hover:bg-blue-700"
        >
          Record dose
        </button>
        {!showOverride && (
          <button
            onClick={() => setShowOverride(true)}
            className="text-xs text-slate-500 hover:text-slate-700"
          >
            Blocked? Add override reason
          </button>
        )}
        <button onClick={onCancel} className="text-xs text-slate-400 hover:text-slate-600">
          Cancel
        </button>
      </div>
    </div>
  );
}

export default function ImmunizationTab({ patientId, editable }: ImmunizationTabProps) {
  const [doses, setDoses] = useState<ImmunizationDose[]>([]);
  const [loading, setLoading] = useState(true);
  const [openFormDoseId, setOpenFormDoseId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const cal = await ImmunizationAPI.getCalendar(patientId);
      setDoses(cal.doses);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to load immunization calendar");
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAdminister = async (
    doseId: string,
    date: string,
    batchNo: string,
    overrideReason: string
  ) => {
    try {
      const result = await ImmunizationAPI.administerDose(patientId, doseId, {
        administered_date: date,
        batch_no: batchNo || undefined,
        override_reason: overrideReason || undefined,
      });
      if (isDoseBlocked(result)) {
        toast.error(`Blocked: ${result.reasons.join("; ")}`);
        return;
      }
      toast.success("Dose recorded");
      setOpenFormDoseId(null);
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to record dose");
    }
  };

  const handleCatchUp = async (doseId: string) => {
    try {
      const recorded = await ImmunizationAPI.catchUp(patientId, doseId);
      toast.success(`Recorded dose ${recorded.dose_number} of ${recorded.series_key} and unblocked dependents`);
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Catch-up failed");
    }
  };

  if (loading) {
    return <div className="p-6 text-sm text-slate-500">Loading immunization calendar…</div>;
  }

  if (doses.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 text-sm text-slate-500">
        No immunization calendar yet.
      </div>
    );
  }

  const bySeries = doses.reduce<Record<string, ImmunizationDose[]>>((acc, d) => {
    (acc[d.series_key] ||= []).push(d);
    return acc;
  }, {});

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 sm:p-6 space-y-6">
      {Object.entries(bySeries).map(([seriesKey, seriesDoses]) => (
        <div key={seriesKey}>
          <h3 className="text-sm font-semibold text-slate-700 mb-2">{seriesKey}</h3>
          <div className="space-y-2">
            {seriesDoses
              .sort((a, b) => a.dose_number - b.dose_number)
              .map((dose) => (
                <div key={dose.dose_id} className="border border-slate-100 rounded-xl p-3">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div>
                      <div className="text-sm font-medium text-slate-800">
                        {dose.vaccine_name} (dose {dose.dose_number})
                        {dose.schedule_source === "IAP" && (
                          <span className="ml-2 text-[10px] uppercase tracking-wide text-slate-400">IAP</span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500">Due {dose.due_date}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusChip status={dose.display_status} />
                      {editable && dose.display_status === "blocked" && (
                        <button
                          onClick={() => handleCatchUp(dose.dose_id)}
                          className="text-xs font-medium bg-amber-500 text-white px-3 py-1.5 rounded-lg hover:bg-amber-600"
                        >
                          One-click catch-up
                        </button>
                      )}
                      {editable &&
                        (dose.display_status === "due" ||
                          dose.display_status === "overdue" ||
                          dose.display_status === "blocked") && (
                          <button
                            onClick={() =>
                              setOpenFormDoseId(openFormDoseId === dose.dose_id ? null : dose.dose_id)
                            }
                            className="text-xs font-medium text-slate-600 border border-slate-200 px-3 py-1.5 rounded-lg hover:bg-slate-50"
                          >
                            Record
                          </button>
                        )}
                    </div>
                  </div>
                  {dose.display_status === "blocked" && dose.blocking_reasons.length > 0 && (
                    <ul className="mt-2 text-xs text-rose-600 list-disc list-inside">
                      {dose.blocking_reasons.map((r, i) => (
                        <li key={i}>{r}</li>
                      ))}
                    </ul>
                  )}
                  {dose.display_status === "administered" && dose.override_reason && (
                    <div className="mt-2 text-xs text-slate-500">
                      Overridden: {dose.override_reason}
                    </div>
                  )}
                  {openFormDoseId === dose.dose_id && (
                    <AdministerForm
                      dose={dose}
                      onCancel={() => setOpenFormDoseId(null)}
                      onSubmit={(date, batchNo, overrideReason) =>
                        handleAdminister(dose.dose_id, date, batchNo, overrideReason)
                      }
                    />
                  )}
                </div>
              ))}
          </div>
        </div>
      ))}
    </div>
  );
}
